// firebaseDataProvider.ts

import {
    DataProvider,
    RaRecord,
    Identifier,
    CreateParams,
    CreateResult,
    GetOneParams,
    GetOneResult,
    UpdateParams,
    UpdateResult,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyResult,
    GetManyReferenceParams,
    GetManyReferenceResult,
    UpdateManyParams,
    UpdateManyResult,
    DeleteManyParams,
    DeleteManyResult,
    PaginationPayload,
    SortPayload,
} from 'react-admin';
import { WithFieldValue, DocumentReference } from 'firebase/firestore';

import { firebaseApp } from '../firebase';
import {
    getFirestore,
    collection,
    CollectionReference,
    doc,
    getDoc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    Timestamp,
    startAfter,
    limit,
    WhereFilterOp,
    getCountFromServer,
    UpdateData,
    setDoc
} from 'firebase/firestore';

// Initialize Firestore
const db = getFirestore(firebaseApp);

/**
 * Optionally map your react-admin resource name to a Firestore collection name.
 * For example, if your react-admin resource is 'customers' but your Firestore
 * collection is also named 'customers', you can simply return the same string.
 */
const getFirestoreCollection = (resource: string): string => {
    // Customize this mapping if needed.
    return resource;
};

/**
 * Helper: Applies client-side sorting.
 */
const sortData = <RecordType extends RaRecord = any>(
    data: RecordType[],
    sort?: SortPayload
): RecordType[] => {
    if (!sort) return data;
    const { field, order } = sort;
    return data.sort((a, b) => {
        if (a[field] < b[field]) return order === 'ASC' ? -1 : 1;
        if (a[field] > b[field]) return order === 'ASC' ? 1 : -1;
        return 0;
    });
};

/**
 * Helper: Applies client-side pagination.
 */
const paginateData = <RecordType extends RaRecord = any>(
    data: RecordType[],
    pagination: PaginationPayload
): { paginated: RecordType[]; total: number } => {
    const { page, perPage } = pagination;
    const total = data.length;
    const start = (page - 1) * perPage;
    const paginated = data.slice(start, start + perPage);
    return { paginated, total };
};

/**
 * Firestore Data Provider for react-admin.
 */
const firebaseDataProvider: DataProvider = {
    // CREATE: Add a new document to the specified collection.
    create: async <
        RecordType extends { id: Identifier } = any,
        ResultRecordType extends RaRecord = RecordType
    >(
        resource: string,
        params: CreateParams<RecordType>
    ): Promise<CreateResult<ResultRecordType>> => {
        // Check that an id is provided in params.data.
        if (params.data.id === undefined || params.data.id === null) {
            throw new Error("An id must be provided in params.data");
        }

        // Convert the custom id to a string. Firestore requires document IDs to be strings.
        const customId = `${params.data.id}`;

        // Get the collection reference.
        const collectionName = getFirestoreCollection(resource);
        const collRef = collection(db, collectionName) as CollectionReference<RecordType>;

        // Create the document reference using the custom id.
        const docRef = doc(collRef, customId);

        // Prepare the data to save.
        const dataToSave = {
            ...params.data,
            // createdAt: Timestamp.now(), // Uncomment if you want to add a timestamp.
        } as unknown as WithFieldValue<RecordType>;

        // Save the document with setDoc.
        await setDoc(docRef, dataToSave);

        // Return the saved data including the custom id.
        return { data: dataToSave } as unknown as CreateResult<ResultRecordType>;
    },



    // GET ONE: Retrieve a single document by id.
    //
    // For the `categories` resource, admin-created records live in `sync/override/extra/{id}`
    // and that is the admin's source of truth — `categories/{id}` is regenerated from there
    // on every sync run and is also enriched with sync-managed fields (resized image
    // variants, assetOriginal). Reading from the override means the Edit form sees the
    // pristine admin input and never accidentally re-saves a generated variant back
    // through the form's image field. Synced categories don't have an override/extra
    // doc and are read from categories/, except when hidden: the sync removes hidden
    // categories from there, so they are read from their sync/override/categories doc.
    getOne: async <RecordType extends RaRecord = any>(
        resource: string,
        params: GetOneParams
    ): Promise<GetOneResult<RecordType>> => {
        if (resource === 'categories') {
            const overrideRef = doc(db, 'sync/override/extra', String(params.id));
            const overrideSnap = await getDoc(overrideRef);
            if (overrideSnap.exists()) {
                return { data: { id: overrideSnap.id, ...overrideSnap.data() } } as unknown as GetOneResult<RecordType>;
            }

            const [categorySnap, syncOverrideSnap] = await Promise.all([
                getDoc(doc(db, 'categories', String(params.id))),
                getDoc(doc(db, 'sync/override/categories', String(params.id))),
            ]);
            // isHidden is only stored on the override doc.
            const isHidden = syncOverrideSnap.exists() && syncOverrideSnap.data().isHidden === true;
            if (categorySnap.exists()) {
                return { data: { id: categorySnap.id, ...categorySnap.data(), isHidden } } as unknown as GetOneResult<RecordType>;
            }
            if (syncOverrideSnap.exists()) {
                return { data: { id: syncOverrideSnap.id, ...syncOverrideSnap.data() } } as unknown as GetOneResult<RecordType>;
            }
            throw new Error(`Document not found: ${params.id}`);
        }

        const collectionName = getFirestoreCollection(resource);
        const docRef = doc(db, collectionName, String(params.id));
        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error(`Document not found: ${params.id}`);
        }
        return { data: { id: snapshot.id, ...snapshot.data() } } as unknown as GetOneResult<RecordType>;
    },

    // UPDATE: Update an existing document.
    update: async <RecordType extends RaRecord = any>(
        resource: string,
        params: UpdateParams<RecordType>
    ): Promise<UpdateResult<RecordType>> => {
        const collectionName = getFirestoreCollection(resource);
        const docRef = doc(db, collectionName, String(params.id)) as DocumentReference<RecordType>;

        const cleanData = Object.fromEntries(
            Object.entries(params.data).filter(([_, v]) => v !== undefined)
        );

        if (resource === 'categories' && cleanData.isHidden) {
            // The apps read categories/ directly, so a hidden category must not be written there.
            // CategoryEdit saves the override and then deletes categories/{id}.
            return { data: { ...cleanData, id: params.id } } as UpdateResult<RecordType>;
        }

        if (resource === 'categories' && !(await getDoc(docRef)).exists()) {
            // Unhiding: categories/{id} was removed while hidden, so restore it from the saved
            // record right away. The next sync refreshes its entries.
            await setDoc(docRef, cleanData);
        } else {
            await updateDoc(docRef, cleanData);
        }

        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error(`Document not found after update: ${params.id}`);
        }

        return { data: { ...snapshot.data() } } as UpdateResult<RecordType>;
    },  
    
    // Set: Update an existing document.
    set: async <RecordType extends RaRecord = any>(
        resource: string,
        params: UpdateParams<RecordType>
    ): Promise<UpdateResult<RecordType>> => {
        const collectionName = getFirestoreCollection(resource);
        const docRef = doc(db, collectionName, String(params.id)) as DocumentReference<RecordType>;

        const cleanData = Object.fromEntries(
            Object.entries(params.data).filter(([_, v]) => v !== undefined)
        );

        await setDoc(docRef, cleanData);

        const snapshot = await getDoc(docRef);
        if (!snapshot.exists()) {
            throw new Error(`Document not found after update: ${params.id}`);
        }

        return { data: { ...snapshot.data() } } as UpdateResult<RecordType>;
    },

    // DELETE: Delete a document by id.
    delete: async <RecordType extends RaRecord = any>(
        resource: string,
        params: DeleteParams
    ): Promise<DeleteResult<RecordType>> => {
        const collectionName = getFirestoreCollection(resource);
        const docRef = doc(db, collectionName, String(params.id));
        await deleteDoc(docRef);
        return { data: { id: params.id } } as unknown as DeleteResult<RecordType>;
    },

    getList: async <RecordType extends RaRecord = any>(
        resource: string,
        params: GetListParams
    ): Promise<GetListResult<RecordType>> => {
        const collRef = collection(db, getFirestoreCollection(resource)) as CollectionReference<RecordType>;
        const { field = "time", order = "DESC" } = params.sort || {};
        const { filter = {} } = params;
        const metaFilters = params.meta || {};
        const combinedFilters = { ...filter, ...metaFilters };
        const page = params.pagination?.page ?? 1;
        const perPage = params.pagination?.perPage ?? 10;

        const operatorMap: Record<string, WhereFilterOp> = {
            gte: ">=",
            lte: "<=",
            gt: ">",
            lt: "<",
            in: "in",
            neq: "!=",
            eq: "==",
        };

        const parseValue = (val: any) => {
            if (typeof val === "string" && !isNaN(Date.parse(val))) {
                return Timestamp.fromDate(new Date(val));
            }
            return val;
        };

        const filterConstraints = Object.entries(combinedFilters).map(([rawKey, rawVal]) => {
            const match = rawKey.match(/^(.+?)_(gte|lte|gt|lt|in|neq|eq)$/);
            const fieldName = match ? match[1] : rawKey;
            const op = match ? operatorMap[match[2]] : "==";
            return where(fieldName, op, parseValue(rawVal));
        });

        if (resource === 'categories') {
            // Hidden categories are removed from categories/ and only exist as override docs, so
            // merge them in to keep them listed (and unhideable). The show forms need this too:
            // they match a show's category names against this list, and a missing category would
            // be dropped from the show when its categories are edited.
            // Sorting and paging happen client-side over the merged set.
            const snapshots = await Promise.all([
                getDocs(query(collRef, ...filterConstraints)),
                getDocs(query(collection(db, 'sync/override/extra'), where('isHidden', '==', true), ...filterConstraints)),
                getDocs(query(collection(db, 'sync/override/categories'), where('isHidden', '==', true), ...filterConstraints)),
            ]);
            // Override docs come last so a hidden record wins over a stale categories/ doc.
            const byId = new Map<string, RecordType>();
            for (const snap of snapshots) {
                snap.docs.forEach(d => byId.set(d.id, { ...d.data(), id: d.id } as unknown as RecordType));
            }
            const sorted = sortData(Array.from(byId.values()), { field, order });
            const { paginated, total } = paginateData(sorted, { page, perPage });
            return { data: paginated, total };
        }

        const countQuery = query(collRef, ...filterConstraints);
        const countSnapshot = await getCountFromServer(countQuery);
        const total = countSnapshot.data().count;

        let paginatedQuery;
        if (page > 1) {
            const previousQuery = query(
                collRef,
                ...filterConstraints,
                orderBy(field, order.toLowerCase() as "asc" | "desc"),
                limit((page - 1) * perPage)
            );
            const previousSnapshot = await getDocs(previousQuery);
            const lastVisible = previousSnapshot.docs[previousSnapshot.docs.length - 1];

            paginatedQuery = query(
                collRef,
                ...filterConstraints,
                orderBy(field, order.toLowerCase() as "asc" | "desc"),
                startAfter(lastVisible),
                limit(perPage)
            );
        } else {
            paginatedQuery = query(
                collRef,
                ...filterConstraints,
                orderBy(field, order.toLowerCase() as "asc" | "desc"),
                limit(perPage)
            );
        }

        const snapshot = await getDocs(paginatedQuery);
        const data = snapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
        } as RecordType));

        return { data, total };
    },

    // GET MANY: Retrieve multiple documents by their ids.
    getMany: async <RecordType extends RaRecord = any>(
        resource: string,
        params: GetManyParams
    ): Promise<GetManyResult<RecordType>> => {
        const collectionName = getFirestoreCollection(resource);
        const promises = params.ids.map(id => getDoc(doc(db, collectionName, String(id))));
        const snapshots = await Promise.all(promises);
        const data: RecordType[] = snapshots.map(snap => ({
            id: snap.id,
            ...snap.data(),
        }) as RecordType);
        return { data };
    },

    // GET MANY REFERENCE: Retrieve documents that match a specific reference.
    getManyReference: async <RecordType extends RaRecord = any>(
        resource: string,
        params: GetManyReferenceParams
    ): Promise<GetManyReferenceResult<RecordType>> => {
        const collectionName = getFirestoreCollection(resource);
        const collRef = collection(db, collectionName) as CollectionReference<RecordType>;
        const q = query(collRef, where(params.target, '==', params.id));
        const snapshot = await getDocs(q);
        let data: RecordType[] = snapshot.docs.map(docSnap => ({
            ...docSnap.data(),
        }) as RecordType);
        data = sortData(data, params.sort);
        const { paginated, total } = paginateData(data, params.pagination);
        return { data: paginated, total };
    },

    // UPDATE MANY: Update multiple documents by their ids.
    updateMany: async <RecordType extends RaRecord = any>(
        resource: string,
        params: UpdateManyParams<RecordType>
    ): Promise<UpdateManyResult> => {
        return {};
    },

    // DELETE MANY: Delete multiple documents by their ids.
    deleteMany: async <RecordType extends RaRecord = any>(
        resource: string,
        params: DeleteManyParams
    ): Promise<DeleteManyResult> => {
        const collectionName = getFirestoreCollection(resource);
        const promises = params.ids.map(id => {
            const docRef = doc(db, collectionName, String(id));
            return deleteDoc(docRef).then(() => id);
        });
        const deletedIds = await Promise.all(promises);
        return { data: deletedIds };
    },
};

export default firebaseDataProvider;
